import { useEffect, useRef, useState } from 'react';
import {
  Grid2,
  Button,
  Stack,
  Typography,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  ListItemText
} from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import RequiredIndicator from 'components/@extended/RequiredIndicator';
import MainCard from 'components/MainCard';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from 'hooks/useAuth';
import Snackbar from 'utils/Snackbar';

import { createNOS, updateNOS, createNewNOSVersion } from '../../services/nos.service';
import { getAllPerformanceCriteria } from '../../services/performanceCriteria.service';

const STORAGE_KEY = 'nos_draft';
interface NOSFormValues {
  nosCode: string;
  nosName: string;
  version: string;
  totalMarks: number | string;
  pcIds: string[];
}

const AddNOS = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organization } = useAuth();

  const isEdit = location.state?.isEdit || false;
  const editData = location.state?.data || null;
  const createNewVersionFrom = location.state?.createNewVersionFrom || null;
  const isNewVersionMode = Boolean(createNewVersionFrom);

  const [performanceCriteria, setPerformanceCriteria] = useState<any[]>([]);
  const [loadingPCs, setLoadingPCs] = useState(false);
  const isMounted = useRef(false);

  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [version, setVersion] = useState('');
  const [versionError, setVersionError] = useState('');
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [pendingHelpers, setPendingHelpers] = useState<FormikHelpers<NOSFormValues> | null>(null);
  const [qpPage] = useState(1); 

  const fetchPCs = async () => {
    if (!organization?._id) return;

    try {
      setLoadingPCs(true);
      const params = {
        limit: 200,
        skip: 1
      };

      const res = await getAllPerformanceCriteria(params);

      if (res?.status === 200) {
        const list = res?.data?.data?.performanceCriteria || res?.data?.data || [];
        setPerformanceCriteria(list);
      }
    } catch (err) {
      console.error('Failed to load Performance Criteria:', err);
      Snackbar('Failed to load Performance Criteria', 'error');
    } finally {
      setLoadingPCs(false);
    }
  };

  useEffect(() => {
    if (organization?._id) {
      fetchPCs();
    }
  }, [organization?._id]);

  const normalizePcIds = (raw: any): string[] => {
    if (!Array.isArray(raw)) return [];
    return raw
      .map((item) => (typeof item === 'string' ? item : item?._id))
      .filter((id): id is string => Boolean(id));
  };

  const getInitialValues = (): NOSFormValues => {
    const source = isEdit ? editData : createNewVersionFrom;
    if (source) {
      return {
        nosCode: source?.nosCode || '',
        nosName: source?.nosName || '',
        version: source?.version ?? '1',
        totalMarks: source?.totalMarks || '',
        pcIds: normalizePcIds(source?.pcIds)
      };
    }

    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored) {
      try {
        const draft = JSON.parse(stored);
        if (draft?.organizationId === organization?._id) {
          return {
            ...draft.values,
            version: draft.values?.version ?? '1',
            pcIds: normalizePcIds(draft.values?.pcIds)
          };
        }
      } catch {
        console.warn('Invalid NOS draft');
      }
    }

    return {
      nosCode: '',
      nosName: '',
      version: '1',
      totalMarks: '',
      pcIds: []
    };
  };

  const initialValues = getInitialValues();

  const validationSchema = Yup.object({
    nosCode: Yup.string()
      .trim()
      .min(3, 'NOS Code must be at least 3 characters')
      .max(20, 'NOS Code too long')
      .required('NOS Code is required'),
    nosName: Yup.string()
      .trim()
      .min(5, 'NOS Name must be at least 5 characters')
      .max(150, 'NOS Name too long')
      .required('NOS Name is required'),
    version: Yup.string().optional(),
    pcIds: Yup.array()
      .of(Yup.string().required())
      .min(1, 'At least one Performance Criteria is required')
  });

  const handleSubmit = async (values: NOSFormValues, helpers: FormikHelpers<NOSFormValues>) => {
    helpers.setSubmitting(true);

    const payload = {
      nosCode: values.nosCode.trim(),
      nosName: values.nosName.trim(),
      version: values.version?.trim() || '1',
      pcIds: normalizePcIds(values.pcIds)
    };

    try {
      let res;

      if (isNewVersionMode && createNewVersionFrom?._id) {
        const nosId = createNewVersionFrom._id;
        res = await createNewNOSVersion(nosId, { nosName: payload.nosName, pcIds: payload.pcIds });
        Snackbar('New NOS version created successfully', 'success');
      } else if (isEdit && editData) {
        const nosId = editData?.nosId || editData?._id;
        res = await updateNOS(nosId, payload);
        Snackbar('NOS updated successfully', 'success');
      } else {
        res = await createNOS(payload);
        Snackbar('NOS created successfully', 'success');
      }

      if (res?.status === 200 || res?.status === 201) {
        localStorage.removeItem(STORAGE_KEY);
        navigate(-1);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to save NOS';

      if (msg.includes('already exists') && !isEdit) {
        setPendingPayload(payload);
        setPendingHelpers(helpers);
        setVersion(values.version?.trim() || '');
        setVersionError('');
        setVersionDialogOpen(true);
        helpers.setSubmitting(false);
        return;
      }
      if (isEdit && msg?.includes('in use by one or more Qualification Packs')) {
        Snackbar('Use "Create new version" from the list to add a new version instead of editing.', 'info');
      }
      Snackbar(msg, 'info');
    } finally {
      helpers.setSubmitting(false);
    }
  };

  const handleVersionSubmit = async () => {
    if (!version.trim()) {
      setVersionError('Version is required');
      return;
    }

    setVersionDialogOpen(false);

    try {
      const res = await createNOS({ ...pendingPayload, version: version.trim() });

      if (res?.status === 200 || res?.status === 201) {
        localStorage.removeItem(STORAGE_KEY);
        Snackbar('NOS created successfully', 'success');
        navigate(-1);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to save NOS';
      Snackbar(msg, 'error');
    } finally {
      pendingHelpers?.setSubmitting(false);
    }
  };

  return (
    <Grid2 container>
      <Grid2 size={12}>
        <MainCard
          content={false}
          title={
            <Stack direction="row" spacing={2} alignItems="center">
              <BackToggle show />
              <Stack>
                <Typography variant="h5">
                  {isNewVersionMode ? 'Create new NOS version' : isEdit ? 'Edit NOS' : 'Add NOS'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isNewVersionMode
                    ? 'Create a new version (existing QPs keep using the old version)'
                    : isEdit
                      ? 'Update National Occupational Standard'
                      : 'Create a new NOS'}
                </Typography>
              </Stack>
            </Stack>
          }
        >
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isValid, dirty, isSubmitting }) => {
              useEffect(() => {
                if (isEdit) return;

                if (!isMounted.current) {
                  isMounted.current = true;
                  return;
                }

                if (dirty) {
                  const draftData = {
                    organizationId: organization?._id,
                    values
                  };

                  localStorage.setItem(STORAGE_KEY, JSON.stringify(draftData));
                }
              }, [values, dirty, isEdit, organization?._id]);
              return (
                <form noValidate onSubmit={handleSubmit}>
                  <Box p={3}>
                    <Grid2 container spacing={2.5}>
                      {/* NOS Code */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>
                          NOS Code <RequiredIndicator />
                        </InputLabel>
                        <OutlinedInput
                          fullWidth
                          name="nosCode"
                          value={values.nosCode}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.nosCode && Boolean(errors.nosCode)}
                          placeholder="e.g. AGR/N0001"
                        />
                        <FormHelperText error>{touched.nosCode && errors.nosCode}</FormHelperText>
                      </Grid2>

                      {/* NOS Name */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>
                          NOS Name <RequiredIndicator />
                        </InputLabel>
                        <OutlinedInput
                          fullWidth
                          name="nosName"
                          value={values.nosName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.nosName && Boolean(errors.nosName)}
                          placeholder="e.g. Prepare land for organic farming"
                        />
                        <FormHelperText error>{touched.nosName && errors.nosName}</FormHelperText>
                      </Grid2>

                      {/* Version */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>Version</InputLabel>
                        <OutlinedInput
                          fullWidth
                          name="version"
                          value={values.version}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={isNewVersionMode}
                          placeholder={isNewVersionMode ? 'Auto-assigned' : 'e.g. 1'}
                        />
                      </Grid2>

                      {/* PCs Multi-select */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>
                          Performance Criteria (PC) <RequiredIndicator />
                        </InputLabel>
                        <Select
                          fullWidth
                          multiple
                          name="pcIds"
                          value={values.pcIds || []}
                          onChange={(e: SelectChangeEvent<string[]>) =>
                            setFieldValue('pcIds', e.target.value as string[])
                          }
                          onBlur={handleBlur}
                          disabled={loadingPCs || performanceCriteria.length === 0}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected || selected.length === 0) return 'Select PCs';
                            const labels = performanceCriteria
                              .filter((pc) => selected.includes(pc._id))
                              .map((pc) => `${pc.pcCode} - ${pc.pcName}`);
                            return labels.join(', ');
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select PCs
                          </MenuItem>
                          {performanceCriteria.map((pc) => (
                            <MenuItem key={pc._id} value={pc._id}>
                              <Checkbox checked={values.pcIds.indexOf(pc._id) > -1} />
                              <ListItemText primary={`${pc.pcCode} - ${pc.pcName}`} />
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText error>{touched.pcIds && errors.pcIds}</FormHelperText>
                      </Grid2>
                      {/* Marks */}
                      {/* <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>Total Marks</InputLabel>
                        <OutlinedInput
                          fullWidth
                          type="number"
                          name="totalMarks"
                          disabled={true}
                          value={values.totalMarks}
                          // onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.totalMarks && Boolean(errors.totalMarks)}
                          placeholder="e.g. 30"
                        />
                        <FormHelperText error>{touched.totalMarks && errors.totalMarks}</FormHelperText>
                      </Grid2> */}
                    </Grid2>

                    <Stack direction="row" justifyContent="flex-end" mt={4} spacing={2}>
                      <Button variant="outlined" onClick={() => navigate(-1)} disabled={isSubmitting}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained" disabled={isSubmitting || !isValid || (isEdit && !dirty)}>
                        {isNewVersionMode ? 'Create new version' : isEdit ? 'Update NOS' : 'Create NOS'}
                      </Button>
                    </Stack>
                  </Box>
                </form>
              );
            }}
          </Formik>
        </MainCard>
      </Grid2>
      <Dialog open={versionDialogOpen} onClose={() => setVersionDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>NOS code already exists</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            An NOS with this code and version already exists. Enter a different version to create a new one.
          </Typography>
          <InputLabel sx={{ mb: 0.5 }}>
            Version <RequiredIndicator />
          </InputLabel>
          <OutlinedInput
            fullWidth
            value={version}
            onChange={(e) => {
              setVersion(e.target.value);
              if (e.target.value.trim()) setVersionError('');
            }}
            placeholder="e.g. 2.0"
            error={Boolean(versionError)}
            autoFocus
          />
          {versionError && <FormHelperText error>{versionError}</FormHelperText>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setVersionDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleVersionSubmit}>
            Create with Version
          </Button>
        </DialogActions>
      </Dialog>
    </Grid2>
  );
};

export default AddNOS;
